/**
 * D&D 5e SRD Beasts
 * Used for Druid Wild Shape, Find Familiar spell, and other creature summoning.
 */

import type { Size, AbilityScores } from './common';

export type CreatureType =
  | 'aberration'
  | 'beast'
  | 'celestial'
  | 'construct'
  | 'dragon'
  | 'elemental'
  | 'fey'
  | 'fiend'
  | 'giant'
  | 'humanoid'
  | 'monstrosity'
  | 'ooze'
  | 'plant'
  | 'undead';

export type ChallengeRating =
  | '0'
  | '1/8'
  | '1/4'
  | '1/2'
  | '1'
  | '2'
  | '3'
  | '4'
  | '5'
  | '6'
  | '7'
  | '8';

export interface Beast {
  id: string;
  name: string;
  size: Size;
  type: CreatureType;
  alignment: string;
  armorClass: number;
  hitPoints: {
    average: number;
    formula: string;
  };
  speed: {
    walk?: number;
    fly?: number;
    swim?: number;
    climb?: number;
    burrow?: number;
  };
  abilityScores: AbilityScores;
  /** Challenge rating as a string (e.g., "1/4", "1", "2") */
  challengeRating: ChallengeRating;
  /** XP value */
  experiencePoints: number;
  /** Special senses like darkvision, blindsight */
  senses?: string[];
  /** Languages the creature understands */
  languages?: string[];
  /** Special traits/abilities */
  traits?: BeastTrait[];
  /** Actions the creature can take */
  actions?: BeastAction[];
  /** Whether this creature can be a familiar (Find Familiar spell) */
  canBeFamiliar?: boolean;
  /** Whether this creature can be used for Wild Shape */
  wildShapeEligible?: boolean;
  /** Source of the creature data */
  source: string;
}

export interface BeastTrait {
  name: string;
  description: string;
}

export interface BeastAction {
  name: string;
  description: string;
  attackBonus?: number;
  damage?: string;
  damageType?: string;
}

/**
 * CR to numeric value conversion for sorting/filtering
 */
export const CR_VALUES: Record<ChallengeRating, number> = {
  '0': 0,
  '1/8': 0.125,
  '1/4': 0.25,
  '1/2': 0.5,
  '1': 1,
  '2': 2,
  '3': 3,
  '4': 4,
  '5': 5,
  '6': 6,
  '7': 7,
  '8': 8,
};

/**
 * Wild Shape CR limits by Druid level
 * Level 2: CR 1/4, no flying/swimming
 * Level 4: CR 1/2, no flying
 * Level 8: CR 1
 */
export const WILD_SHAPE_CR_LIMITS: Record<number, { maxCR: number; canFly: boolean; canSwim: boolean }> = {
  2: { maxCR: 0.25, canFly: false, canSwim: false },
  4: { maxCR: 0.5, canFly: false, canSwim: true },
  8: { maxCR: 1, canFly: true, canSwim: true },
};
